import { createI18n, i18next } from '@multaqa/i18n'
import arHrSettings from '@multaqa/i18n/locales/ar/hr-settings.json'
import enHrSettings from '@multaqa/i18n/locales/en/hr-settings.json'
import { LOGO_WRITE_DURATION_MS, LogoLoader } from '@multaqa/ui/components/ui/logo-loader'
import { QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createQueryClient } from '@/lib/query-client'
import { initSentry } from '@/lib/sentry'
import { applyTheme, useUiStore } from '@/stores/ui'
import { routeTree } from './routeTree.gen'
import './styles.css'

initSentry()

const RESOURCES = {
  en: {
    'hr-settings': enHrSettings,
  },
  ar: {
    'hr-settings': arHrSettings,
  },
}

// Before render, both of them. i18n init after the first paint shows a flash of
// raw translation keys; theme after the first paint shows a flash of white.
createI18n({ resources: RESOURCES })
applyTheme(useUiStore.getState().theme)

const queryClient = createQueryClient()

/*
 * Shown while any route's guard or lazy chunk resolves - on a hard reload of
 * an authenticated route that is the session check, the app's real boot
 * screen. i18next initialised above, before the first render, so the label
 * translates here; `common` is the default namespace in both apps.
 */
function RoutePending() {
  // Full-screen pending state: the write animation sits in the middle of the
  // viewport, not top-left. `min-h-svh` tracks the mobile dynamic viewport.
  return (
    <div className="flex min-h-svh items-center justify-center">
      <LogoLoader size="lg" label={i18next.t('state.loading')} />
    </div>
  )
}

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPendingComponent: RoutePending,
  // Hold the pending screen until the mark has finished writing itself. The
  // router would otherwise swap the content in mid-stroke, and the loader
  // cannot keep itself mounted - the router owns that. 500ms is the library
  // default and is shorter than the animation.
  defaultPendingMinMs: LOGO_WRITE_DURATION_MS,
  defaultPreload: 'intent',
  // The router does not cache data — TanStack Query does. Setting this to 0
  // stops the two keeping separate, divergent copies of the same response.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const container = document.getElementById('root')

if (!container) {
  throw new Error('Root element #root is missing from index.html')
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
